<script lang="ts" setup>
import { ItemInterface } from '../../models/ItemModel'
import BackIcon from '../icons/BackIcon.vue'
import { onBeforeUnmount, onMounted, ref } from 'vue'
import ASpinner from '../common/ASpinner.vue'
import AButton from '../common/AButton.vue'
import { updateOrderItemQuantity } from '../../services/orderApi'
import { showToast } from '../../services/toast'
import { useI18n } from 'vue-i18n'
import { Modal } from 'bootstrap'
import { isNetworkFailure } from '../../services/https'
import ATextarea from '../common/ATextarea.vue'

const props = defineProps<{
  item: ItemInterface
}>()

const emit = defineEmits<{
  closeDetails: []
  updateItem: [ItemInterface]
  item: [ItemInterface]
}>()

const { t } = useI18n()

const quantity = ref<number>(0)
const isUpdating = ref<boolean>(false)

const imageModalRef = ref<HTMLElement | null>(null)
const commentModalRef = ref<HTMLElement | null>(null)

type BsModal = InstanceType<typeof Modal>
const imageModal = ref<BsModal | null>(null)
const commentModal = ref<BsModal | null>(null)

const comment = ref<string>(props.item.info || '')

const showContinue = ref<boolean>(false)
const showRetry = ref<boolean>(false)

onMounted(() => {
  setQuantity()

  if (imageModalRef.value) {
    imageModal.value = new Modal(imageModalRef.value, {
      backdrop: true,
      keyboard: true,
    })
  }

  if (commentModalRef.value) {
    commentModal.value = new Modal(commentModalRef.value, {
      backdrop: true,
      keyboard: true,
    })
  }
})

onBeforeUnmount(() => {
  imageModal.value?.dispose()
  commentModal.value?.dispose()
})

function setQuantity() {
  const quantityDifference = props.item.quantity_total - props.item.quantity_collected
  if (quantityDifference > 0) {
    quantity.value = quantityDifference
  } else {
    quantity.value = 0
  }
}

function openCommentModal() {
  commentModal.value?.show()
}

function openImageModal() {
  imageModal.value?.show()
}

function closeCommentModal() {
  commentModal.value?.hide()
}

async function updateQuantity(): Promise<void> {
  isUpdating.value = true
  showRetry.value = false
  let newQuantity = Number(quantity.value) + props.item.quantity_collected
  if (newQuantity < 0) {
    newQuantity = 0
  }
  updateOrderItemQuantity(props.item.id, newQuantity)
    .then((parsedItem) => {
      emit('updateItem', parsedItem)
      showContinue.value = true
      showToast({ message: t('quantity-update-success'), variant: 'success', noTitle: true })
      emit('closeDetails')
    })
    .catch((error) => {
      if (isNetworkFailure(error)) {
        showRetry.value = true
      }
    })
    .finally(() => {
      quantity.value = 0
      isUpdating.value = false
    })
}

async function updateComment(): Promise<void> {
  isUpdating.value = true
  updateOrderItemQuantity(props.item.id, props.item.quantity_collected, comment.value)
    .then((parsedItem) => {
      emit('updateItem', parsedItem)
      showToast({ message: t('item-comment-update-success'), variant: 'success', noTitle: true })
      closeCommentModal()
    })
    .finally(() => (isUpdating.value = false))
}
</script>

<template>
  <div class="item-details">
    <div class="header">
      <div class="container">
        <div class="header-inner">
          <button class="header-logo" @click="emit('closeDetails')">
            <BackIcon />
            <span>{{ $t('items') }}</span>
          </button>
          <span />
        </div>
      </div>
    </div>
    <div class="container">
      <div class="item-item pl-0">
        <div>
          <div>
            <b>{{ props.item.title }}</b>
          </div>
          <div>
            <small
              ><b>{{ props.item.order_item_number }}</b></small
            >
          </div>
          <div>
            <small>{{ props.item.barcode.main }}</small>
          </div>
        </div>
        <button
          v-if="props.item.image_url"
          class="item-item__preview mb-auto"
          @click="openImageModal()"
        />
      </div>
      <div class="table-responsive">
        <table class="item-details__table m-0">
          <tbody>
            <tr>
              <td>
                <div
                  class="d-flex align-items-center justify-content-center text-center gap-6 flex-wrap"
                >
                  <span>{{ $t('amount') }}:</span>
                  <b class="h5 mb-0"
                    >{{ props.item.quantity_total }}
                    <span class="fw-400" style="font-size: 16px">{{
                      props.item.measure_unit
                    }}</span></b
                  >
                </div>
              </td>
              <td>
                <div
                  class="d-flex align-items-center justify-content-center text-center gap-6 flex-wrap"
                >
                  <span>{{ $t('collected') }}:</span>
                  <b class="h5 mb-0"
                    >{{ props.item.quantity_collected }}
                    <span class="fw-400" style="font-size: 16px">{{
                      props.item.measure_unit
                    }}</span></b
                  >
                </div>
              </td>
            </tr>
            <tr>
              <td colspan="2" class="text-start">
                <div v-if="props.item.info">{{ `${$t('note')}: ${props.item.info}` }}</div>
                <div>
                  <button class="order-comment" :disabled="isUpdating" @click="openCommentModal()">
                    {{ $t(props.item.info ? 'edit-item-comment' : 'add-item-comment') }}
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="props.item.location">
              <td colspan="3" class="text-start">
                {{ `${$t('location-in-warehouse')}: ${props.item.location}` }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="py-16">
        <ASpinner v-model="quantity" :label="String($t('quantity'))" :auto-focus="true">
          <template #extraButton>
            <AButton class="btn-warning w-100" :disabled="isUpdating" @click="updateQuantity">
              {{ $t('confirm') }}
            </AButton>
          </template>
        </ASpinner>
      </div>
      <div v-if="showRetry" class="pb-16">
        <AButton class="w-100 btn-danger" :disabled="isUpdating" @click="updateQuantity">
          {{ $t('try-again') }}
        </AButton>
      </div>
      <div v-if="showContinue" class="pb-16">
        <AButton class="w-100" @click="emit('closeDetails')">
          {{ $t('continue') }}
        </AButton>
      </div>
    </div>
  </div>

  <div
    v-if="props.item.image_url"
    ref="imageModalRef"
    class="modal fade"
    tabindex="-1"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content item-details__modal">
        <div class="modal-body p-0 position-relative">
          <div class="item-details__image">
            <img class="w-100" :src="props.item.image_url" :alt="props.item.title" />
          </div>
          <button type="button" class="item-details__close" data-bs-dismiss="modal">
            {{ $t('close') }}
          </button>
          <div class="p-12">
            <div class="item-details__place">
              {{ $t('location-in-warehouse') }}
            </div>
            <div>
              <b>{{ props.item.location || '-' }}</b>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div ref="commentModalRef" class="modal fade" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-body">
          <h4 class="mb-32 text-center">{{ $t('item-comment') }}</h4>
          <ATextarea v-model="comment" :label="$t('comment')" class="mb-32" />
          <div class="d-flex flex-wrap gap-12">
            <button type="button" class="btn btn-outline-secondary flex-1" data-bs-dismiss="modal">
              {{ $t('close') }}
            </button>
            <button
              type="button"
              class="btn btn-primary flex-1"
              :disabled="isUpdating"
              @click="updateComment"
            >
              {{ $t('save') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
